import { supabaseClient } from './supabaseUtils.mjs';
import type { CommunityPostIntent } from './communityPostIntent';
import { stripImageMetadataInBrowser } from './stripImageMetadataClient';

// Types
export interface Post {
  post_id: string;
  title?: string | null;
  content: string;
  category: string;
  created_at: string;
  updated_at: string;
  likes_count: number;
  comments_count: number;
  companion_intent?: CommunityPostIntent | null;
  geo_zipcode?: string | null;
  is_anonymous?: boolean;
  flares: string[];
  favorite_products?: FavoriteProductAttachment[] | null;
  user?: {
    first_name: string;
    last_name: string;
    profile_photo: string;
    zipcode?: string;
    is_companion?: boolean;
  } | null;
  media?: PostMedia[];
  video_topic?: string | null;
  video_feed_filters?: string[];
  comments?: Comment[];
  permissions: PostPermissions;
}

export interface PostPermissions {
  canEdit: boolean;
  canDelete: boolean;
}

export interface CreatePostInput {
  title?: string | null;
  inspiration_question?: string;
  content: string;
  category: string;
  flares?: string[];
  favorite_products?: FavoriteProductAttachment[] | null;
  is_anonymous?: boolean;
}

export type UpdatePostInput = Partial<
  Pick<Post, 'title' | 'content' | 'category' | 'flares' | 'favorite_products' | 'is_anonymous'>
>;

export interface PostMedia {
  media_id: string;
  post_id: string;
  media_url: string;
  media_type: 'image' | 'video';
  created_at: string;
}

export interface FavoriteProductAttachment {
  product_id: string;
  title: string;
  store_name?: string | null;
  price?: number | null;
  price_text?: string | null;
  image_link?: string | null;
  product_link?: string | null;
}

export interface Comment {
  comment_id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  likes_count?: number;
  parent_comment_id?: string | null;
  replies?: Comment[];
  user?: {
    first_name: string;
    last_name: string;
    profile_photo: string;
    is_companion?: boolean;
  };
}

const IOS_VIDEO_PREVIEW_FRAGMENT = '#t=0.001';

export function getVideoPreviewUrl(mediaUrl: string): string {
  if (typeof mediaUrl !== 'string') {
    return '';
  }

  const normalizedUrl = mediaUrl.trim();
  if (!normalizedUrl) {
    return '';
  }

  // iOS Safari often renders a black placeholder when no poster is provided.
  // Adding a tiny time fragment forces first-frame extraction without thumbnails.
  if (normalizedUrl.includes('#')) {
    return normalizedUrl;
  }

  return `${normalizedUrl}${IOS_VIDEO_PREVIEW_FRAGMENT}`;
}

export type PostAuthorDisplay = {
  name: string;
  initials: string;
  avatarUrl: string | null;
  isAnonymous: boolean;
};

const DEFAULT_INITIAL = 'C';

export type MentionSuggestion = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  profile_photo?: string | null;
};

async function syncCommentMentions(
  commentId: string,
  method: 'POST' | 'DELETE' = 'POST',
): Promise<void> {
  try {
    const response = await fetch(
      `/api/community/comments/${encodeURIComponent(commentId)}/mentions`,
      { method },
    );
    await readCommunityApiResponse<{ success: true; mentions?: number }>(response);
  } catch (error) {
    if (method === 'DELETE') {
      // Do not delete the source comment while an actor-identifying mention row
      // may remain if the live schema does not cascade that relationship.
      throw error;
    }

    // The comment is already persisted for POST sync, so notifications are
    // best-effort and must not make the comment UI report a false failure.
    console.error('[community] Failed to synchronize comment mentions', {
      commentId,
      method,
      error,
    });
  }
}

function sanitizeSearchTerm(value: string): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/[(),]/g, ' ').replace(/\s+/g, ' ').trim();
}

export async function searchMentionableUsers(
  query: string,
  { limit = 8 }: { limit?: number } = {},
): Promise<MentionSuggestion[]> {
  const sanitized = sanitizeSearchTerm(query);
  if (!sanitized) return [];
  try {
    const { data, error } = await supabaseClient
      .from('community_public_profiles')
      .select('id, display_name, profile_photo')
      .ilike('display_name', `%${sanitized}%`)
      .limit(Math.max(1, Math.min(limit, 15)));
    if (error) {
      console.error('Error searching community profiles:', error);
      return [];
    }
    return (data ?? []).map(user => ({
      id: user.id,
      first_name: user.display_name || 'Community Member',
      last_name: '',
      profile_photo: user.profile_photo || null,
    }));
  } catch (error) {
    console.error('Exception searching mentionable users:', error);
    return [];
  }
}
export function getPostAuthorDisplay(post: Pick<Post, 'is_anonymous' | 'user'>): PostAuthorDisplay {
  if (post?.is_anonymous) {
    return {
      name: 'Anonymous User',
      initials: 'AU',
      avatarUrl: null,
      isAnonymous: true,
    };
  }

  const firstName = post?.user?.first_name?.trim() ?? '';
  const lastName = post?.user?.last_name?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Community Member';
  const initialsCandidate = firstName?.[0] || lastName?.[0] || fullName[0] || DEFAULT_INITIAL;

  return {
    name: fullName,
    initials: initialsCandidate.toUpperCase(),
    avatarUrl: post?.user?.profile_photo || null,
    isAnonymous: false,
  };
}

export interface PostFilter {
  category?: string;
  searchQuery?: string;
  userId?: string;
  inMyArea?: boolean;
  userZipcode?: string;
  radiusMiles?: number;
  sort?: 'latest' | 'discussed';
  mediaType?: 'video';
  mediaTopic?: string;
  feedFilter?: 'deals' | 'tips' | 'alerts';
}

// Get current user ID
export async function getCurrentUserId() {
  console.log("getCurrentUserId called");
  try {
    const { data, error } = await supabaseClient.auth.getUser();
    
    if (error) {
      console.error("Error in getCurrentUserId:", error);
      return null;
    }
    
    console.log("getCurrentUserId result:", data?.user?.id || "No user ID found");
    return data?.user?.id;
  } catch (error) {
    console.error("Exception in getCurrentUserId:", error);
    return null;
  }
}

// Fetch all posts with optional filtering
export interface FetchPostsOptions {
  page?: number;
  pageSize?: number;
}

export interface FetchPostsResult {
  posts: Post[];
  total: number;
}

const MAX_PAGE_SIZE = 50;

async function readCommunityApiResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.message === 'string'
        ? payload.message
        : typeof payload?.error === 'string'
          ? payload.error
          : `Community request failed (${response.status})`;
    throw new Error(message);
  }

  return payload as T;
}

export async function fetchPosts(filter?: PostFilter, options?: FetchPostsOptions): Promise<FetchPostsResult> {
  const pageSize = Math.max(1, Math.min(options?.pageSize ?? 10, MAX_PAGE_SIZE));
  const page = Math.max(1, options?.page ?? 1);
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  if (filter?.category && filter.category !== 'All Posts') {
    params.set('category', filter.category);
  }

  if (filter?.sort === 'discussed') params.set('sort', 'discussed');
  if (filter?.feedFilter) params.set('feedFilter', filter.feedFilter);
  if (filter?.mediaType === 'video') params.set('mediaType', 'video');
  if (filter?.mediaType === 'video' && filter?.mediaTopic) params.set('mediaTopic', filter.mediaTopic);

  const searchQuery = filter?.searchQuery?.trim();
  if (searchQuery) {
    params.set('searchQuery', searchQuery);
  }

  // The server derives the current user from the session. Never send a user ID
  // that could be changed to enumerate another member's anonymous posts.
  if (filter?.userId) {
    params.set('mine', 'true');
  }

  if (filter?.inMyArea && filter.userZipcode) {
    params.set('inMyArea', 'true');
    params.set('userZipcode', filter.userZipcode);
    params.set('radiusMiles', String(filter.radiusMiles ?? 25));
  }

  const response = await fetch(`/api/community/posts?${params.toString()}`);
  const payload = await readCommunityApiResponse<FetchPostsResult>(response);

  return {
    posts: Array.isArray(payload.posts) ? payload.posts : [],
    total: typeof payload.total === 'number' ? payload.total : 0,
  };
}

// Fetch a single post with its media and comments
export async function fetchPost(postId: string): Promise<Post> {
  if (!postId) {
    throw new Error('Post ID is required');
  }

  const response = await fetch(`/api/community/posts/${encodeURIComponent(postId)}`);
  const payload = await readCommunityApiResponse<{ post: Post }>(response);
  return payload.post;
}

export async function fetchPostComments(postId: string): Promise<Comment[]> {
  const post = await fetchPost(postId);
  return Array.isArray(post.comments) ? post.comments : [];
}

// Create a new post
export async function createPost(post: CreatePostInput): Promise<Post> {
  const response = await fetch('/api/community/posts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(post),
  });
  const payload = await readCommunityApiResponse<{ post: Post }>(response);
  return payload.post;
}

export async function updatePost(
  postId: string,
  updates: UpdatePostInput,
): Promise<Post> {
  if (!postId) {
    throw new Error('Post ID is required to update a post');
  }

  const cleanedUpdates = Object.fromEntries(
    Object.entries(updates ?? {}).filter(([_, value]) => value !== undefined)
  ) as UpdatePostInput;

  if (Object.keys(cleanedUpdates).length === 0) {
    throw new Error('No updates provided for the post');
  }

  const response = await fetch(`/api/community/posts/${encodeURIComponent(postId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cleanedUpdates),
  });
  const payload = await readCommunityApiResponse<{ post: Post }>(response);
  return payload.post;
}

async function cleanupUnregisteredPostMedia(postId: string, storagePath: string): Promise<void> {
  try {
    const response = await fetch('/api/community/media', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postId, storagePath }),
    });
    await readCommunityApiResponse<{ success: true }>(response);
  } catch (error) {
    console.error('[community] Failed to clean up unregistered post media', {
      postId,
      error,
    });
  }
}

// Upload media for a post
export async function uploadPostMedia(postId: string, file: File, onProgress?: (progress: number) => void) {
  if (!postId) {
    throw new Error('Post ID is required to upload media');
  }

  const { data: authData, error: authError } = await supabaseClient.auth.getSession();
  if (authError || !authData.session?.user?.id) {
    throw new Error('You must be logged in to upload files');
  }

  // Shelfies are taken inside stores and phones write GPS into the file, so the
  // metadata comes off before the bytes leave the device. The server strips
  // again when the upload is registered.
  const uploadFile = await stripImageMetadataInBrowser(file);

  const rawExtension = uploadFile.name.split('.').pop()?.toLowerCase() ?? '';
  const safeExtension = /^[a-z0-9]{1,10}$/.test(rawExtension) ? rawExtension : 'bin';
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 15)}.${safeExtension}`;
  // Public object URLs must not include the author's user ID: anonymous posts
  // are identified only by their non-author-revealing post ID.
  const storagePath = `${postId}/${fileName}`;

  onProgress?.(0);
  const { error: uploadError } = await supabaseClient.storage
    .from('post-media')
    .upload(storagePath, uploadFile, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data: publicUrlData } = supabaseClient.storage
    .from('post-media')
    .getPublicUrl(storagePath);
  const mediaUrl = publicUrlData?.publicUrl;

  if (!mediaUrl) {
    await cleanupUnregisteredPostMedia(postId, storagePath);
    throw new Error('Failed to get public URL for uploaded media');
  }

  const mediaType: PostMedia['media_type'] = uploadFile.type.startsWith('image/') ? 'image' : 'video';

  try {
    const response = await fetch('/api/community/media', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postId, mediaUrl, mediaType }),
    });
    const payload = await readCommunityApiResponse<{ media: PostMedia }>(response);
    onProgress?.(100);
    return payload.media;
  } catch (error) {
    // Avoid leaving an unreferenced object when the trusted API rejects the
    // attachment or the database insert fails.
    await cleanupUnregisteredPostMedia(postId, storagePath);
    throw error;
  }
}

export async function removePostMedia(postId: string, mediaId: string, _mediaUrl?: string) {
  if (!postId || !mediaId) {
    throw new Error('Post ID and media ID are required to remove media');
  }

  const response = await fetch('/api/community/media', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postId, mediaId }),
  });
  await readCommunityApiResponse<{ success: true }>(response);
  return true;
}

// Add a comment to a post
export async function addComment(
  comment: Omit<Comment, 'comment_id' | 'created_at' | 'updated_at' | 'likes_count'>,
) {
  console.log("addComment called with data:", comment);
  
  try {
    const payload = {
      ...comment,
      parent_comment_id: comment.parent_comment_id ?? null,
    };
    // Insert the comment
    const { data, error } = await supabaseClient
      .from('comments')
      .insert(payload)
      .select();

    if (error) {
      console.error('Error adding comment:', error);
      throw error;
    }

    if (data && data[0]) {
      await refreshPostMetrics(comment.post_id);
    }

    const inserted = data?.[0] as Comment | undefined;
    if (!inserted) {
      throw new Error('Comment insert failed.');
    }

    await syncCommentMentions(inserted.comment_id);

    return {
      ...inserted,
      likes_count: typeof inserted.likes_count === 'number' ? inserted.likes_count : 0,
    };
  } catch (error) {
    console.error("Exception in addComment:", error);
    throw error;
  }
}

export async function updateComment(
  commentId: string,
  updates: Pick<Comment, 'content'>,
  options: { userId?: string | null; isAdmin?: boolean }
) {
  try {
    let query = supabaseClient
      .from('comments')
      .update({ content: updates.content })
      .eq('comment_id', commentId);

    if (!options?.isAdmin && options?.userId) {
      query = query.eq('user_id', options.userId);
    }

    const { data, error } = await query.select();

    if (error) {
      console.error('Error updating comment:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('Comment not found or permission denied.');
    }

    const updatedComment = data[0] as Comment;

    await syncCommentMentions(updatedComment.comment_id);

    return updatedComment;
  } catch (error) {
    console.error('Exception in updateComment:', error);
    throw error;
  }
}

export async function removeComment(
  commentId: string,
  options: { userId?: string | null; isAdmin?: boolean }
): Promise<{ postId: string }> {
  try {
    const { data: existingComment, error: fetchError } = await supabaseClient
      .from('comments')
      .select('post_id')
      .eq('comment_id', commentId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching comment to delete:', fetchError);
      throw fetchError;
    }

    if (!existingComment) {
      throw new Error('Comment not found.');
    }

    await syncCommentMentions(commentId, 'DELETE');

    let query = supabaseClient.from('comments').delete().eq('comment_id', commentId);

    if (!options?.isAdmin && options?.userId) {
      query = query.eq('user_id', options.userId);
    }

    const { error: deleteError } = await query;

    if (deleteError) {
      console.error('Error deleting comment:', deleteError);
      throw deleteError;
    }

    await refreshPostMetrics(existingComment.post_id);

    return { postId: existingComment.post_id };
  } catch (error) {
    console.error('Exception in removeComment:', error);
    throw error;
  }
}

// Guest likes use a server-verified cookie rather than a Youdle account.
export async function getGuestLikeStatuses(postIds: string[]): Promise<Record<string, boolean>> {
  if (!postIds.length) return {};
  const params = new URLSearchParams({ postIds: postIds.join(',') });
  const response = await fetch('/api/community/posts/guest-likes?' + params.toString(), {
    credentials: 'same-origin', cache: 'no-store',
  });
  const payload = await readCommunityApiResponse<{ likedByPost: Record<string, boolean> }>(response);
  return payload.likedByPost;
}

export async function toggleGuestLike(postId: string): Promise<{ liked: boolean; likesCount: number }> {
  const response = await fetch('/api/community/posts/guest-likes', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postId }),
  });
  return await readCommunityApiResponse<{ liked: boolean; likesCount: number }>(response);
}

// Like or unlike a post
export async function toggleLike(
  postId: string,
  userId: string
): Promise<{ liked: boolean; likesCount?: number }> {
  console.log(`toggleLike called for post ${postId} by user ${userId}`);
  
  try {
    // Check if the user has already liked the post
    const { data: existingLike, error: checkError } = await supabaseClient
      .from('likes')
      .select('like_id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking like status:', checkError);
      throw checkError;
    }

    if (existingLike) {
      // Unlike the post
      console.log("User has already liked the post, removing like");
      const { error: unlikeError } = await supabaseClient
        .from('likes')
        .delete()
        .eq('like_id', existingLike.like_id);

      if (unlikeError) {
        console.error('Error unliking post:', unlikeError);
        throw unlikeError;
      }

      const latestCounts = await refreshPostMetrics(postId);
      return { liked: false, likesCount: latestCounts?.likes };
    } else {
      // Like the post
      console.log("User has not liked the post, adding like");
      const { error: likeError } = await supabaseClient
        .from('likes')
        .insert({
          post_id: postId,
          user_id: userId
        });

      if (likeError) {
        console.error('Error liking post:', likeError);
        throw likeError;
      }

      const latestCounts = await refreshPostMetrics(postId);
      return { liked: true, likesCount: latestCounts?.likes };
    }
  } catch (error) {
    console.error("Exception in toggleLike:", error);
    throw error;
  }
}

export async function toggleCommentLike(
  commentId: string,
  postId: string,
  userId: string,
): Promise<{ liked: boolean; likesCount?: number }> {
  console.log(`toggleCommentLike called for comment ${commentId} on post ${postId}`);

  try {
    const { data: existingLike, error: checkError } = await supabaseClient
      .from('comment_likes')
      .select('like_id')
      .eq('comment_id', commentId)
      .eq('user_id', userId)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking comment like status:', checkError);
      throw checkError;
    }

    if (existingLike) {
      const { error: unlikeError } = await supabaseClient
        .from('comment_likes')
        .delete()
        .eq('like_id', existingLike.like_id);

      if (unlikeError) {
        console.error('Error removing comment like:', unlikeError);
        throw unlikeError;
      }
    } else {
      const { error: likeError } = await supabaseClient
        .from('comment_likes')
        .insert({
          comment_id: commentId,
          post_id: postId,
          user_id: userId,
        });

      if (likeError) {
        console.error('Error liking comment:', likeError);
        throw likeError;
      }
    }

    const { data: updatedComment, error: fetchError } = await supabaseClient
      .from('comments')
      .select('likes_count')
      .eq('comment_id', commentId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching updated comment counts:', fetchError);
      throw fetchError;
    }

    const safeCount =
      typeof updatedComment?.likes_count === 'number' ? updatedComment.likes_count : 0;

    return {
      liked: !existingLike,
      likesCount: safeCount,
    };
  } catch (error) {
    console.error('Exception in toggleCommentLike:', error);
    throw error;
  }
}

export async function fetchUserCommentLikes(
  postId: string,
  userId: string,
): Promise<Record<string, boolean>> {
  try {
    const { data, error } = await supabaseClient
      .from('comment_likes')
      .select('comment_id')
      .eq('post_id', postId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user comment likes:', error);
      throw error;
    }

    const rows = (data ?? []) as Array<{ comment_id?: string | null }>;
    return rows.reduce<Record<string, boolean>>((acc, row) => {
      if (row?.comment_id) {
        acc[row.comment_id] = true;
      }
      return acc;
    }, {});
  } catch (error) {
    console.error('Exception in fetchUserCommentLikes:', error);
    throw error;
  }
}

async function refreshPostMetrics(postId: string): Promise<{ likes: number; comments: number } | null> {
  try {
    const response = await fetch(`/api/community/posts/${postId}/metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message =
        typeof payload?.error === 'string' ? payload.error : `Failed to refresh metrics (${response.status})`;
      throw new Error(message);
    }

    const data = (await response.json()) as { likes?: number; comments?: number };
    return {
      likes: typeof data.likes === 'number' ? data.likes : 0,
      comments: typeof data.comments === 'number' ? data.comments : 0,
    };
  } catch (error) {
    console.error('[community] Failed to refresh post metrics', error);
    return null;
  }
}

// Check if a user has liked a post
export async function checkLikeStatus(postId: string, userId: string) {
  console.log(`Checking like status for post ${postId} by user ${userId}`);
  
  try {
    const { data, error } = await supabaseClient
      .from('likes')
      .select('like_id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking like status:', error);
      throw error;
    }

    const liked = !!data;
    console.log(`User has ${liked ? 'liked' : 'not liked'} the post`);
    return { liked };
  } catch (error) {
    console.error('Exception in checkLikeStatus:', error);
    return { liked: false }; // Default to not liked in case of error
  }
}

// Delete a post
export async function deletePost(postId: string) {
  const response = await fetch(`/api/community/posts/${postId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      typeof payload?.error === 'string' ? payload.error : `Failed to delete post (${response.status})`;
    throw new Error(message);
  }

  const result = await response.json().catch(() => ({ success: true }));
  return result;
}

// Fetch recent media
export async function fetchRecentMedia(limit = 12) {
  const safeLimit = Math.max(1, Math.min(Math.floor(limit), 50));
  const response = await fetch(`/api/community/media?limit=${safeLimit}`);
  const payload = await readCommunityApiResponse<{ media: Array<PostMedia & { post?: { post_id: string; title: string | null } | null }> }>(response);
  return Array.isArray(payload.media) ? payload.media : [];
}

// Get post categories
export const POST_CATEGORIES = [
  'All Posts',
  'Deals & Discounts',
  'Stock Updates',
  'Reviews & Recommendations',
  'ISO Requests',
  'Food Donation and Distribution'
];

// Update post counts directly
export async function updatePostCounts(postId: string) {
  return Boolean(await refreshPostMetrics(postId));
}
